export default function ZipVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - ZipVEdit Archive Manager
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for ZipVEdit.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I open an archive?
              </h3>
              <p className="mt-1">
                Tap <strong>Open</strong> on the home screen to pick an
                archive from your device, or tap an archive file in any file
                manager — ZipVEdit registers itself as a handler for common
                archive types, so it will appear in the &quot;Open with&quot;
                list.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I extract files?
              </h3>
              <p className="mt-1">
                After opening an archive, tap <strong>Extract all</strong>{" "}
                to extract to the same folder as the archive, or choose a
                different destination. You can also tap a single file
                inside the archive and pick <strong>Extract</strong> to
                save just that file.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create a ZIP from files?
              </h3>
              <p className="mt-1">
                Two ways. (1) Tap <strong>Create</strong> on the home
                screen and pick your files. (2) Select files in any app
                (Photos, Files, Drive, etc.) and tap Share — pick{" "}
                <strong>Compress with ZipVEdit</strong> in the share
                sheet. Choose a preset (Email-friendly, WhatsApp,
                Smallest) or set a custom compression level, add a
                password if you want, and tap Create.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Which archive formats are supported?
              </h3>
              <p className="mt-1">
                ZipVEdit opens ZIP, TAR, GZ, TAR.GZ, BZ2, 7Z, and RAR
                archives. The free tier creates ZIP with standard
                compression. The Pro upgrade adds AES-256 encrypted ZIP
                and unlimited output to advanced formats.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does ZipVEdit work offline?
              </h3>
              <p className="mt-1">
                Yes. Everything — opening, extracting, creating, and
                previewing — runs entirely on your device. Network access
                is used only for ads and the cross-promotion banner.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I preview files inside an archive?
              </h3>
              <p className="mt-1">
                Yes. Tap any file inside an open archive. Text (TXT, MD,
                JSON, XML, YAML, CSV, LOG), images (PNG, JPG, WEBP, GIF),
                and PDFs can all be previewed without extracting. Other
                file types prompt you to extract first.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do password-protected archives work?
              </h3>
              <p className="mt-1">
                When you open a password-protected archive, ZipVEdit asks
                for the password. You can opt in to remember the password
                on your device — toggle biometric unlock to require
                fingerprint or face unlock before the password is
                retrieved. Saved passwords never leave your device and
                can be cleared from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. A one-time Pro
                upgrade from Settings removes all ads and unlocks AES-256
                encryption, batch compression, cloud destinations, and
                unlimited advanced-format output.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing or an archive won&apos;t open. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the
                latest version from Google Play. If a specific archive is
                the problem, email us with the archive attached — we may be
                able to diagnose corrupted or unusually encoded files.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details
            in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/zipvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/zipvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/zipvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/zipvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> ZipVEdit Archive Manager
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.zipvedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
